import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import ValueProps from "@/components/ValueProps";
import TrustStrip from "@/components/TrustStrip";
import LanguageBanner from "@/components/LanguageBanner";
import Reveal from "@/components/Reveal";
import { useSeo, useLocaleAlternates } from "@/hooks/use-seo";
import { useJsonLd } from "@/hooks/use-json-ld";
import { useLang } from "@/i18n/LanguageContext";

// Below-fold sections — code-split to slim the initial bundle. Each renders
// inside a Reveal wrapper that already reserves space, so suspense fallback
// is just an empty placeholder (no CLS).
const ThreeLayerPositioning = lazy(() => import("@/components/ThreeLayerPositioning"));
const ProductFamilyBlock = lazy(() =>
  import("@/components/DemandConcentration").then((m) => ({ default: m.ProductFamilyBlock })),
);
const UseCaseBlock = lazy(() =>
  import("@/components/DemandConcentration").then((m) => ({ default: m.UseCaseBlock })),
);


const DecisionPath = lazy(() => import("@/components/DecisionPath"));
const RecentlySourced = lazy(() => import("@/components/RecentlySourced"));
const MostRequestedSolutions = lazy(() => import("@/components/MostRequestedSolutions"));
const Suppliers = lazy(() => import("@/components/Suppliers"));
const Categories = lazy(() => import("@/components/Categories"));
const Comparison = lazy(() => import("@/components/Comparison"));
const SolutionsSection = lazy(() => import("@/components/SolutionsSection"));

const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Timelines = lazy(() => import("@/components/Timelines"));
const CountryCoverage = lazy(() => import("@/components/CountryCoverage"));
const CaseStudy = lazy(() => import("@/components/CaseStudy"));
const QuoteForm = lazy(() => import("@/components/QuoteForm"));
const WhatsAppCTA = lazy(() => import("@/components/WhatsAppCTA"));
const FinalCTA = lazy(() => import("@/components/FinalCTA"));
const Footer = lazy(() => import("@/components/Footer"));

// Objection-handling proof blocks — code-split to keep them off the
// LCP critical path. Placed below the demand-concentration row and
// before the comparison/CTA blocks so visitors see "what a quote
// looks like" before being asked to commit.
const SampleQuotes = lazy(() =>
  import("@/components/proof").then((m) => ({ default: m.SampleQuotes })),
);
const LandedCostExamples = lazy(() =>
  import("@/components/proof").then((m) => ({ default: m.LandedCostExamples })),
);
const DeliveryTimelines = lazy(() =>
  import("@/components/proof").then((m) => ({ default: m.DeliveryTimelines })),
);
const FulfilledRequests = lazy(() =>
  import("@/components/proof").then((m) => ({ default: m.FulfilledRequests })),
);

// Non-critical UI — defer until idle / first interaction so they don't
// fight for main-thread time during LCP.
const FloatingWhatsApp = lazy(() => import("@/components/FloatingWhatsApp"));
const StickyMobileCTA = lazy(() => import("@/components/StickyMobileCTA"));
const BackToTop = lazy(() => import("@/components/BackToTop"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));

const SITE_ORIGIN = "https://www.switzh.com";
const INLANGUAGE: Record<string, string> = { en: "en-US", es: "es-419", pt: "pt-BR" };

/** Reserve vertical space for lazy chunks so the page doesn't collapse / shift. */
const SectionFallback = ({ minH = 320 }: { minH?: number }) => (
  <div aria-hidden="true" style={{ minHeight: minH }} />
);

const Index = () => {
  const { t, prefix, lang, localePath } = useLang();
  const alternates = useLocaleAlternates("/");
  const canonical = `${SITE_ORIGIN}${prefix || ""}/`;
  useSeo({
    title: t("seo.home.title"),
    description: t("seo.home.desc"),
    canonical,
    alternates,
    lang,
  });

  // Mount non-critical widgets only after browser is idle (or after first
  // user interaction) — keeps them out of the LCP / TBT critical path.
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    let done = false;
    const ready = () => {
      if (done) return;
      done = true;
      setDeferredReady(true);
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(ready, { timeout: 3000 });
    } else {
      window.setTimeout(ready, 1500);
    }
    // Also wake them up on first user interaction in case idle never fires.
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll"];
    events.forEach((e) => window.addEventListener(e, ready, { once: true, passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, ready));
    };
  }, []);

  // Refined Organization entity (replaces the static one in index.html on
  // hydration so social/search crawlers that execute JS get the richer data).
  const orgLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_ORIGIN}/#organization`,
      name: "Switzh",
      url: `${SITE_ORIGIN}/`,
      logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/UrTKU5J3W0N2kOKdooNDwcz7yUc2/social-images/social-1776394095401-4B52A4EC-5E7E-417A-ACCD-3CF3A02CCDF0.webp",
      description: t("seo.home.desc"),
      areaServed: ["Colombia", "Peru", "Mexico", "Chile", "Ecuador", "Panama", "Latin America"],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "quotes@switzh.com",
        availableLanguage: ["English", "Spanish", "Portuguese"],
      },
    }),
    [t],
  );

  const websiteLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_ORIGIN}/#website`,
      url: `${SITE_ORIGIN}/`,
      name: "Switzh",
      description: t("seo.home.desc"),
      inLanguage: INLANGUAGE[lang] || INLANGUAGE.en,
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    }),
    [lang, t],
  );

  const faqLd = useMemo(() => {
    const faqs: Record<"en" | "es" | "pt", Array<{ q: string; a: string }>> = {
      en: [
        { q: "How fast can I get a quote?", a: "Most quote requests receive a response within a few hours during U.S. business hours. Quotes include product pricing, freight, duties, and delivery timing to your country." },
        { q: "What countries do you deliver to?", a: "We deliver across Latin America, including Colombia, Mexico, Peru, Chile, Brazil, Panama, and Ecuador, with full DDP (delivered duty paid) pricing." },
        { q: "What does DDP pricing include?", a: "DDP (Delivered Duty Paid) pricing includes the product cost, U.S. handling, international freight, customs duties, taxes, and final delivery to your site. No hidden fees added later." },
        { q: "Do I need a part number to request a quote?", a: "No. You can send a SKU, part number, product link, spec sheet, photo, or a description of what you need. We help validate the right part before quoting." },
        { q: "Which brands do you source?", a: "We source ABB, Schneider Electric, Siemens, Rockwell Automation, Eaton, and other major industrial electrical brands through U.S. distribution and aftermarket networks." },
        { q: "Are you an authorized distributor?", a: "Switzh is an independent sourcing partner. We are not an authorized distributor; we source through U.S. distribution and aftermarket channels." },
      ],
      es: [
        { q: "¿Qué tan rápido recibo una cotización?", a: "La mayoría de las solicitudes reciben respuesta en pocas horas en horario hábil de EE. UU. Las cotizaciones incluyen precio del producto, flete, aranceles y tiempo de entrega a tu país." },
        { q: "¿A qué países entregan?", a: "Entregamos en toda Latinoamérica, incluyendo Colombia, México, Perú, Chile, Brasil, Panamá y Ecuador, con precio DDP total (entregado con derechos pagados)." },
        { q: "¿Qué incluye el precio DDP?", a: "El precio DDP incluye costo del producto, manejo en EE. UU., flete internacional, aranceles, impuestos y entrega final a tu sitio. Sin costos ocultos." },
        { q: "¿Necesito el número de parte para cotizar?", a: "No. Puedes enviar un SKU, número de parte, enlace, ficha técnica, foto o descripción. Validamos la pieza correcta antes de cotizar." },
        { q: "¿Qué marcas suministran?", a: "Suministramos ABB, Schneider Electric, Siemens, Rockwell Automation, Eaton y otras marcas industriales a través de redes de distribución y mercado secundario en EE. UU." },
        { q: "¿Son distribuidor autorizado?", a: "Switzh es un socio independiente de abastecimiento. No somos distribuidor autorizado; abastecemos a través de canales de distribución y aftermarket en EE. UU." },
      ],
      pt: [
        { q: "Em quanto tempo recebo uma cotação?", a: "A maioria das solicitações recebe resposta em poucas horas no horário comercial dos EUA. As cotações incluem preço do produto, frete, taxas e prazo de entrega ao seu país." },
        { q: "Para quais países vocês entregam?", a: "Entregamos em toda a América Latina, incluindo Colômbia, México, Peru, Chile, Brasil, Panamá e Equador, com preço DDP total (entregue com direitos pagos)." },
        { q: "O que inclui o preço DDP?", a: "O preço DDP inclui custo do produto, manuseio nos EUA, frete internacional, taxas alfandegárias, impostos e entrega final no seu local. Sem custos ocultos." },
        { q: "Preciso do número da peça para cotar?", a: "Não. Você pode enviar SKU, número da peça, link, ficha técnica, foto ou descrição. Validamos a peça correta antes de cotar." },
        { q: "Quais marcas vocês fornecem?", a: "Fornecemos ABB, Schneider Electric, Siemens, Rockwell Automation, Eaton e outras marcas industriais por meio de redes de distribuição e mercado secundário nos EUA." },
        { q: "Vocês são distribuidor autorizado?", a: "A Switzh é um parceiro independente de sourcing. Não somos distribuidores autorizados; fornecemos através de canais de distribuição e aftermarket nos EUA." },
      ],
    };
    const list = faqs[(lang as "en" | "es" | "pt")] || faqs.en;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${canonical}#faq`,
      inLanguage: INLANGUAGE[lang] || INLANGUAGE.en,
      mainEntityOfPage: canonical,
      mainEntity: list.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
  }, [lang, canonical]);

  useJsonLd("organization", orgLd);
  useJsonLd("website", websiteLd);
  useJsonLd("home-faq", faqLd);


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <LanguageBanner />
      <main>
        {/* Above-the-fold — eager. */}
        <Reveal><Hero /></Reveal>
        <Reveal><ValueProps /></Reveal>
        <Reveal><TrustStrip /></Reveal>

        {/* Below-the-fold — lazy chunks with reserved space fallback. */}
        <Suspense fallback={<SectionFallback minH={420} />}>
          <Reveal><ThreeLayerPositioning /></Reveal>
          <Reveal><ProductFamilyBlock source="home_demand" /></Reveal>
          <Reveal><UseCaseBlock source="home_demand" /></Reveal>
          {/* Proof block 1: sample quotes — show format & magnitude before asking to commit */}
          <Reveal><SampleQuotes /></Reveal>
          <Reveal><DecisionPath /></Reveal>
          <Reveal><RecentlySourced /></Reveal>
          <Reveal><Suppliers /></Reveal>
          <Reveal><Categories /></Reveal>
          <Reveal><MostRequestedSolutions /></Reveal>
          <Reveal><SolutionsSection /></Reveal>
          {/* Proof block 2: landed-cost transparency before comparison */}
          <Reveal><LandedCostExamples /></Reveal>
          <Reveal><Comparison /></Reveal>
          
          <Reveal><HowItWorks /></Reveal>
          <Reveal><Timelines /></Reveal>
          {/* Proof block 3: delivery cadence reinforces fulfillment reliability */}
          <Reveal><DeliveryTimelines /></Reveal>
          <Reveal><CountryCoverage /></Reveal>
          {/* Proof block 4: outcome cards reinforce real switchgear+LATAM scenarios */}
          <Reveal><FulfilledRequests /></Reveal>
          <Reveal><CaseStudy /></Reveal>
          <Reveal><QuoteForm /></Reveal>
          <Reveal><WhatsAppCTA /></Reveal>
          <Reveal><FinalCTA /></Reveal>
        </Suspense>
      </main>
      <Suspense fallback={<SectionFallback minH={240} />}>
        <Reveal><Footer /></Reveal>
      </Suspense>

      {/* Deferred non-critical widgets — only mount after idle / first interaction. */}
      {deferredReady && (
        <Suspense fallback={null}>
          <FloatingWhatsApp />
          <BackToTop />
          <StickyMobileCTA href={localePath("/request-quote")} source="home_sticky" />
          <CookieConsent />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
